import { useCallback, useEffect, useState } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import i18n from "@/lib/i18n";

export type TemplateDto = {
  templateId: string;
  fileName: string;
};

function sortTemplates(templates: TemplateDto[]): TemplateDto[] {
  return [...templates].sort((left, right) =>
    left.fileName.localeCompare(right.fileName, undefined, {
      sensitivity: "base",
    }),
  );
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as {
      detail?: string;
      title?: string;
      message?: string;
      error?: string;
    };
    const message = body.detail ?? body.title ?? body.message ?? body.error;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  } catch {
    // Ignore parse failures and fall back to a localized status message.
  }

  if (response.status === 404) return i18n.t("error.code.not_found");
  if (response.status === 403) return i18n.t("error.code.forbidden");
  if (response.status >= 500) return i18n.t("error.code.service_unavailable");
  return i18n.t("error.code.unknown");
}

export function useTemplates() {
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiAuthFetch("/api/templates");
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const payload = (await response.json()) as TemplateDto[];
      setTemplates(sortTemplates(payload));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : i18n.t("error.code.service_unavailable"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const refresh = useCallback(() => {
    void loadTemplates();
  }, [loadTemplates]);

  return { templates, loading, error, refresh };
}
